import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { computeKeyAssignments } from "../utility.service";


export enum ProductStatus
{
    Purchased='purchased',
    Allowed='allowed',
    Locked='locked'

}
export enum ProductType 
{
    Feature='feature',
    Board='Board',
    WesternKeyboard="Western",
    InstrumentSound='instrument',
}
export enum AccessType
{
    LifetimeDemo="lifetime",
    LimitedDemo="limited",
    NewFeatureDemo="newfeature",
    Locked="locked",
    Check="check"
}
export interface ProductItem{
    sku:string;
    desciption?:string;
    type:ProductType,
    accessType:AccessType,
    subscription?:boolean,
    iconSvg?:string,
    videoUrl?:string,
    data?:any
}

interface BoardSKU
{
    sku:string;
    title:string;
    type:ProductType,
    access:AccessType
}
export var productCatalog: ProductItem[] = [];

export class ProductData 
{
    // get allBoardDefs

    constructor(private httpClient:HttpClient)
        {}
    

    // boards
        // flex boards
        // western boards
    
    // instruments
    // features

    private mapAccess(access: string): AccessType {
        switch ((access || '').toLowerCase()) {
            case 'lifetime': return AccessType.LifetimeDemo;
            case 'limited': return AccessType.LimitedDemo;
            case 'newfeature': return AccessType.NewFeatureDemo;
            case 'locked': return AccessType.Locked;
            case 'check': return AccessType.Check;
            default: return AccessType.Check;
        }
    }

    private boardDefCache: any = null;
    private keyboardDefCache: any = null;

    private async loadBoardFile(fileName: string, type: ProductType, items: ProductItem[]) {
        try {
            const data: any = await this.getBoardDetails(fileName, type);
            items.push({
                sku: data.sku || fileName,
                desciption: data.description || data.title || fileName,
                type,
                accessType: this.mapAccess(data.access),
                subscription: false,
                data:data,
                iconSvg: data.imageUrl ? `assets/icon/${data.imageUrl}` : undefined,
                videoUrl: data.videoUrl
            });
        } catch (e) {
            alert(`Missing or invalid board template: ${fileName}`);
        }
    }

    private async loadBoardDef(): Promise<any> {
        if (this.boardDefCache) return this.boardDefCache;
        try {
            this.boardDefCache = await firstValueFrom(
                this.httpClient.get("/assets/boardTemplates/meta/boarddef.json")
            );
        } catch (e) {
            alert("Missing board definition: assets/boardTemplates/meta/boarddef.json");
            this.boardDefCache = {};
        }
        return this.boardDefCache;
    }

    private async loadKeyboardDef(): Promise<any> {
        if (this.keyboardDefCache) return this.keyboardDefCache;
        try {
            this.keyboardDefCache = await firstValueFrom(
                this.httpClient.get("/assets/boardTemplates/meta/keyboarddef.json")
            );
        } catch (e) {
            alert("Missing keyboard definition: assets/boardTemplates/meta/keyboarddef.json");
            this.keyboardDefCache = {};
        }
        return this.keyboardDefCache;
    }

    private indianToWestern(note: string): string {
        if (!note) return note;
        let n = note.trim();
        let octaveOffset = 0;

        if (n.startsWith('.')) {
            octaveOffset -= 1;
            n = n.slice(1);
        }
        if (n.endsWith('.')) {
            octaveOffset -= 1;
            n = n.slice(0, -1);
        }
        const primeCount = (n.match(/'/g) || []).length;
        if (primeCount) {
            octaveOffset += primeCount;
            n = n.replace(/'/g, '');
        }

        const baseMap: Record<string, string> = {
            'S': 'C',
            'r': 'C#',
            'R': 'D',
            'g': 'D#',
            'G': 'E',
            'm': 'F',
            'M': 'F#',
            'P': 'G',
            'd': 'G#',
            'D': 'A',
            'n': 'A#',
            'N': 'B'
        };
        const base = baseMap[n];
        if (!base) return note;
        const octave = 4 + octaveOffset;
        return `${octave}${base}`;
    }

    private normalizeLayout(layout: any) {
        if (!layout) return null;
        const numRows = Number(layout.numRows ?? layout.numRow ?? layout.rows ?? 0);
        const numColumns = Number(layout.numColumns ?? layout.cols ?? layout.col ?? 0);
        return {
            numRows: numRows || 0,
            numColumns: numColumns || 0,
            rowSettings: Array.isArray(layout.rowSettings) ? layout.rowSettings : []
        };
    }

    private mapBoardDef(boardDef: any) {
        if (!boardDef) return boardDef;
        const mapped = JSON.parse(JSON.stringify(boardDef));
        if (mapped.validNotesIndian && Array.isArray(mapped.validNotesIndian)) {
            mapped.validNotes = mapped.validNotesIndian.map((n: string) => this.indianToWestern(n));
        }
        const layouts = ['phonePortrait', 'phoneLandscape', 'tabletLandscape', 'defaultLayout'];
        layouts.forEach((key) => {
            const layout = mapped[key];
            if (layout && Array.isArray(layout.rowSettings)) {
                layout.rowSettings = layout.rowSettings.map((r: any) => ({
                    ...r,
                    start: this.indianToWestern(r.start)
                }));
            }
        });
        const defaultLayout = this.normalizeLayout(mapped.defaultLayout);
        if (mapped.validNotes && defaultLayout && defaultLayout.numRows && defaultLayout.numColumns) {
            const config = {
                validNotes: mapped.validNotes,
                defaultLayout
            };
            const assignments = computeKeyAssignments(config as any, "defaultLayout");
            mapped.assignments = assignments;
            mapped.rows = defaultLayout.numRows;
            mapped.cols = defaultLayout.numColumns;
            mapped.instrSkin = mapped.instrSkin || "flex-piano";
        }
        return mapped;
    }

    async getBoardDetails(fileName: string, type: ProductType) {
        const url = "/assets/boardTemplates/" + fileName;
        const data: any = await firstValueFrom(this.httpClient.get(url));
        if (type === ProductType.WesternKeyboard) {
            const keyboardDef = await this.loadKeyboardDef();
            const key = data.currentBoard;
            const def = (key && keyboardDef[key]) ? keyboardDef[key] : keyboardDef.default;
            if (!def) {
                alert(`Keyboard definition not found for: ${fileName} -> ${key}`);
                data.currentBoardObj = null;
                return data;
            }
            const lowKey = def.lowKey ?? def.kboardIndex ?? 48;
            data.currentBoardObj = { ...def, kboardIndex: lowKey };
            return data;
        }
        const boardDef = await this.loadBoardDef();
        const key = data.currentBoard;
        if (!key || !boardDef[key]) {
            alert(`Board definition not found for: ${fileName} -> ${key}`);
            data.currentBoardObj = null;
            return data;
        }
        data.currentBoardObj = this.mapBoardDef(boardDef[key]);
        return data;
    }

    async buildProductCatalog() {
        const items: ProductItem[] = [];
        let productList: any;
        try {
            productList = await firstValueFrom(this.httpClient.get("/assets/boardTemplates/meta/productlist.json"));
        } catch (e) {
            alert("Missing product list: assets/boardTemplates/meta/productlist.json");
            productCatalog = [];
            return items;
        }

        const westernList = productList?.Western || [];
        const boardList = productList?.Board || [];
        const instruments = productList?.Instruments || [];

        for (const fileName of westernList) {
            await this.loadBoardFile(fileName, ProductType.WesternKeyboard, items);
        }

        for (const fileName of boardList) {
            await this.loadBoardFile(fileName, ProductType.Board, items);
        }

        for (const inst of instruments) {
            items.push({
                sku: inst.sku,
                desciption: inst.title || inst.abbr,
                type: ProductType.InstrumentSound,
                accessType: this.mapAccess(inst.access),
                subscription: false,
                iconSvg: inst.iconSvg,
                videoUrl: inst.videoUrl
            });
        }

        productCatalog = items;
        return items;
    }
    boardList=[];

      
}
